import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, Receipt, Trash2, Pencil } from "lucide-react";
import { formatINR, parseINR } from "@/lib/format";

function monthLabel(monthStr) {
  if (!monthStr) return "—";
  try {
    const [y, m] = monthStr.split("-");
    const d = new Date(parseInt(y, 10), parseInt(m, 10) - 1, 1);
    return d.toLocaleDateString(undefined, { month: "short", year: "numeric" });
  } catch {
    return monthStr;
  }
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return iso;
  }
}

const blank = () => ({
  month: new Date().toISOString().slice(0, 7),
  amount: "",
  due_date: new Date().toISOString().slice(0, 10),
  status: "pending",
  gst_filed: false,
  notes: "",
});

export default function RentTab({ propertyId }) {
  const { user } = useAuth();
  const isAdmin = user && user !== false && user.role === "admin";
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank());

  const load = async () => {
    try {
      const { data } = await api.get(`/properties/${propertyId}/rents`);
      setRecords(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyId]);

  const openNew = () => {
    setEditing(null);
    setForm(blank());
    setOpen(true);
  };

  const openEdit = (r) => {
    setEditing(r);
    setForm({
      month: r.month,
      amount: String(r.amount),
      due_date: r.due_date,
      status: r.status,
      gst_filed: !!r.gst_filed,
      notes: r.notes || "",
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      month: form.month,
      amount: parseFloat(form.amount),
      due_date: form.due_date,
      status: form.status,
      gst_filed: form.gst_filed,
      notes: form.notes,
    };
    if (Number.isNaN(payload.amount)) {
      toast.error("Enter a valid amount");
      return;
    }
    try {
      if (editing) {
        await api.put(`/properties/${propertyId}/rents/${editing.id}`, payload);
        toast.success("Updated");
      } else {
        await api.post(`/properties/${propertyId}/rents`, payload);
        toast.success("Record added");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete record for ${monthLabel(r.month)}?`)) return;
    try {
      await api.delete(`/properties/${propertyId}/rents/${r.id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const totals = records.reduce(
    (acc, r) => {
      acc.total += r.amount;
      if (r.status === "paid") acc.collected += r.amount;
      else acc.pending += r.amount;
      return acc;
    },
    { total: 0, collected: 0, pending: 0 }
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-medium text-stone-900">Rent</h2>
          <p className="text-sm text-stone-500 mt-1">Track monthly rent collections.</p>
        </div>
        {isAdmin && (
          <Button onClick={openNew} className="bg-emerald-900 hover:bg-emerald-800 text-stone-50" data-testid="add-rent-button">
            <Plus className="h-4 w-4 mr-2" strokeWidth={1.5} />
            Add record
          </Button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        {[
          { label: "Total billed", value: totals.total, color: "text-stone-900" },
          { label: "Collected", value: totals.collected, color: "text-emerald-800" },
          { label: "Pending", value: totals.pending, color: "text-amber-700" },
        ].map((s) => (
          <Card key={s.label} className="border-stone-200 bg-white p-5 rounded-xl">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-stone-500">{s.label}</p>
            <p className={`mt-2 text-2xl font-medium ${s.color}`}>
              ₹{s.value.toLocaleString("en-IN", { maximumFractionDigits: 0 })}
            </p>
          </Card>
        ))}
      </div>

      {loading ? (
        <div className="h-40 rounded-xl bg-white border border-stone-200 animate-pulse" />
      ) : records.length === 0 ? (
        <Card className="border border-dashed border-stone-300 bg-white p-12 text-center rounded-xl">
          <Receipt className="h-10 w-10 text-stone-300 mx-auto mb-3" strokeWidth={1.5} />
          <h3 className="text-lg font-medium text-stone-900">No rent records yet</h3>
          <p className="text-stone-500 mt-1 text-sm">Add monthly rent entries to keep your books in order.</p>
        </Card>
      ) : (
        <Card className="border-stone-200 bg-white rounded-xl overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-stone-100 hover:bg-transparent">
                <TableHead className="text-xs uppercase tracking-wider text-stone-400">Month</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-stone-400">Amount</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-stone-400">Due date</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-stone-400">Status</TableHead>
                <TableHead className="text-right text-xs uppercase tracking-wider text-stone-400">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {records.map((r) => (
                <TableRow key={r.id} className="border-stone-100" data-testid={`rent-row-${r.id}`}>
                  <TableCell className="font-medium text-stone-900">{monthLabel(r.month)}</TableCell>
                  <TableCell className="text-stone-700">
                    ₹{Number(r.amount).toLocaleString("en-IN", { maximumFractionDigits: 0 })}
                  </TableCell>
                  <TableCell className="text-stone-600">{formatDate(r.due_date)}</TableCell>
                  <TableCell>
                    {r.status === "paid" ? (
                      <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 hover:bg-emerald-100 rounded-full">Paid</Badge>
                    ) : (
                      <Badge className="bg-amber-100 text-amber-800 border-amber-200 hover:bg-amber-100 rounded-full">Pending</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    {isAdmin ? (
                      <>
                        <Button variant="ghost" size="icon" onClick={() => openEdit(r)} className="text-stone-500 hover:text-emerald-900" data-testid={`edit-rent-${r.id}`}>
                          <Pencil className="h-4 w-4" strokeWidth={1.5} />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => remove(r)} className="text-stone-500 hover:text-red-700" data-testid={`delete-rent-${r.id}`}>
                          <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                        </Button>
                      </>
                    ) : (
                      <span className="text-stone-300 text-xs">—</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="rent-dialog">
          <DialogHeader>
            <DialogTitle className="font-medium">{editing ? "Edit rent record" : "Add rent record"}</DialogTitle>
            <DialogDescription className="text-stone-500">
              {editing ? "Update the details below." : "Log a month's rent and GST status."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="month">Month</Label>
                <Input id="month" type="month" required value={form.month} onChange={(e) => setForm({ ...form, month: e.target.value })} data-testid="rent-month-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="amount">Amount (₹)</Label>
                <Input
                  id="amount"
                  type="text"
                  inputMode="numeric"
                  required
                  value={formatINR(form.amount)}
                  onChange={(e) => setForm({ ...form, amount: parseINR(e.target.value) })}
                  placeholder="e.g. 25,000"
                  data-testid="rent-amount-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="due">Due date</Label>
              <Input id="due" type="date" required value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="rent-due-input" />
            </div>
            <div className="space-y-2">
              <Label>Payment status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="rent-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between rounded-md border border-stone-200 px-4 py-3">
              <div>
                <Label htmlFor="gst" className="text-stone-900">GST received</Label>
                <p className="text-xs text-stone-500 mt-0.5">
                  Toggle on once GST has been received (typically the next month after the return is filed).
                </p>
              </div>
              <Switch id="gst" checked={form.gst_filed} onCheckedChange={(v) => setForm({ ...form, gst_filed: v })} data-testid="rent-gst-switch" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-emerald-900 hover:bg-emerald-800 text-stone-50" data-testid="rent-save">
                {editing ? "Save changes" : "Add record"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
